import { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ArrowUp, ArrowDown, X, Plus, ListChecks, AlertTriangle, Search, UserPlus } from 'lucide-react';
import { ctaFinalApprovalApi } from '../lib/api';
import { toast } from 'sonner';

export const REVIEW_ROLE_LABELS = {
  FINANCE: 'Finance',
  DIRECTOR: 'Director',
  EXEC_DIRECTOR: 'Executive Director',
};

// Stage shape: { role: 'FINANCE' } (role stage — anyone with that role can act)
//           or { user_id, user_name } (user stage — only that exact person can act)
export const DEFAULT_REVIEW_STAGES = {
  CTA: [{ role: 'DIRECTOR' }, { role: 'EXEC_DIRECTOR' }],
  FT: [{ role: 'FINANCE' }, { role: 'DIRECTOR' }, { role: 'EXEC_DIRECTOR' }],
};

export const stageKey = (s) => s.user_id || s.role;
export const stageDisplay = (s) => (s.user_id ? (s.user_name || 'User') : (REVIEW_ROLE_LABELS[s.role] || s.role));
// Map a request's stored review_stages back into editor inputs (round pre-fill)
export const stagesFromRequest = (stored) => (stored || []).map((s) =>
  s.user_id ? { user_id: s.user_id, user_name: s.user_name } : { role: s.role });

// Phase 5: Chairman configures the ordered review flow that runs BEFORE e-signature collection.
// Stages can be classic role stages (Finance / Director / Exec Director) or ANY specific user.
// iter138 — supports an optional `mode` ("sequential" | "parallel") + `onModeChange` handler so
// the Chairman can fan out reviews to all stages at once instead of one-at-a-time.
export function ReviewStagesEditor({ stages, onChange, requestType = 'CTA', requestId, mode = 'sequential', onModeChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [eligibleUsers, setEligibleUsers] = useState({ involved: [], others: [] });
  const [search, setSearch] = useState('');

  const move = (idx, dir) => {
    const next = [...stages];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onChange(next);
  };
  const remove = (idx) => onChange(stages.filter((_, i) => i !== idx));
  const addRole = (role) => onChange([...stages, { role }]);
  const addUser = (u) => {
    if (!stages.some((s) => s.user_id === u.user_id)) {
      onChange([...stages, { user_id: u.user_id, user_name: u.user_name }]);
    }
    setPickerOpen(false);
    setSearch('');
  };
  const openPicker = async () => {
    setPickerOpen(true);
    setSearch('');
    if (requestId && eligibleUsers.involved.length === 0 && eligibleUsers.others.length === 0) {
      try {
        const res = await ctaFinalApprovalApi.getEligibleSignatoryUsers(requestId);
        setEligibleUsers(res.data);
      } catch (e) {
        console.error('Failed to load eligible reviewer users', e);
        toast.error('Could not load user list');
      }
    }
  };

  const missingRoles = Object.keys(REVIEW_ROLE_LABELS).filter((r) => !stages.some((s) => s.role === r));
  const filterUsers = (list) => {
    const q = search.trim().toLowerCase();
    const already = new Set(stages.filter((s) => s.user_id).map((s) => s.user_id));
    return (list || [])
      .filter((u) => !already.has(u.user_id))
      .filter((u) => !q || `${u.user_name} ${u.email || ''} ${u.user_role}`.toLowerCase().includes(q));
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid="review-stages-editor">
      <div className="flex items-center gap-2">
        <ListChecks className="w-4 h-4 text-slate-600" />
        <span className="text-sm font-semibold text-slate-700">Review Stages (before e-signature)</span>
        <Button
          type="button" variant="ghost" size="sm" className="ml-auto h-7 text-xs text-slate-500"
          onClick={() => onChange([...(DEFAULT_REVIEW_STAGES[requestType] || [])])}
          data-testid="review-stages-reset-btn"
        >
          Reset to default
        </Button>
      </div>
      <p className="text-xs text-slate-500">
        Reviewers approve in this order before signatures are collected. A reviewer rejection returns
        the request to you (Chairman).
      </p>

      {/* iter138 — Chairman/Admin/CRD toggle: switch the whole review flow to parallel
          (every stage actionable simultaneously). Sequential preserves the existing
          one-at-a-time behaviour. */}
      {onModeChange && stages.length > 1 && (
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1.5" data-testid="review-stages-mode-row">
          <span className="text-xs font-semibold text-slate-700 mr-1">Review order:</span>
          <button
            type="button"
            className={`text-xs px-2 py-1 rounded border ${mode === 'sequential' ? 'bg-blue-50 border-blue-400 text-blue-800 font-semibold' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
            onClick={() => onModeChange('sequential')}
            data-testid="review-stages-mode-sequential"
          >
            Sequential
          </button>
          <button
            type="button"
            className={`text-xs px-2 py-1 rounded border ${mode === 'parallel' ? 'bg-emerald-50 border-emerald-400 text-emerald-800 font-semibold' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
            onClick={() => onModeChange('parallel')}
            data-testid="review-stages-mode-parallel"
          >
            Parallel — all at once
          </button>
          {mode === 'parallel' && (
            <span className="ml-2 text-[11px] text-emerald-700">
              All reviewers will be notified together; the request advances only after every one of them approves.
            </span>
          )}
        </div>
      )}

      {stages.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="review-stages-empty-warning">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          No review stages — the request will go straight to e-signatures after your approval.
        </div>
      ) : (
        <div className="space-y-1.5">
          {stages.map((s, idx) => (
            <div key={stageKey(s)} className="flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1.5" data-testid={`review-stage-row-${stageKey(s)}`}>
              <Badge variant="secondary" className="text-xs w-6 justify-center">{idx + 1}</Badge>
              <span className="text-sm font-medium flex-1 truncate">{stageDisplay(s)}</span>
              {s.user_id && (
                <Badge variant="outline" className="text-[10px] bg-blue-50 border-blue-200 text-blue-700" data-testid={`review-stage-user-badge-${s.user_id}`}>
                  Specific user
                </Badge>
              )}
              <Button type="button" variant="ghost" size="icon" className="h-6 w-6" disabled={idx === 0}
                onClick={() => move(idx, -1)} data-testid={`review-stage-up-${stageKey(s)}`}>
                <ArrowUp className="w-3.5 h-3.5" />
              </Button>
              <Button type="button" variant="ghost" size="icon" className="h-6 w-6" disabled={idx === stages.length - 1}
                onClick={() => move(idx, 1)} data-testid={`review-stage-down-${stageKey(s)}`}>
                <ArrowDown className="w-3.5 h-3.5" />
              </Button>
              <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:text-red-600"
                onClick={() => remove(idx)} data-testid={`review-stage-remove-${stageKey(s)}`}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        {missingRoles.map((role) => (
          <Button key={role} type="button" variant="outline" size="sm" className="h-7 text-xs"
            onClick={() => addRole(role)} data-testid={`review-stage-add-${role}`}>
            <Plus className="w-3 h-3 mr-1" /> {REVIEW_ROLE_LABELS[role]}
          </Button>
        ))}
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
          onClick={openPicker} data-testid="review-stages-add-user-btn">
          <UserPlus className="w-3 h-3 mr-1" /> Add specific reviewer…
        </Button>
      </div>

      {/* User picker — same two-tab pattern as Add Signatory */}
      <Dialog open={pickerOpen} onOpenChange={(o) => { if (!o) { setPickerOpen(false); setSearch(''); } }}>
        <DialogContent className="max-w-xl" data-testid="review-stages-user-picker">
          <DialogHeader>
            <DialogTitle>Add Reviewer</DialogTitle>
            <DialogDescription>
              Pick a user to add as a review stage. Only that exact person (or an Admin) will be able
              to approve or reject their stage.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              autoFocus
              className="pl-9"
              placeholder="Search by name, email, or role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="review-stages-user-search"
            />
          </div>
          <Tabs defaultValue="involved" className="mt-2">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="involved" data-testid="review-stages-picker-tab-involved">
                Involved in this request ({filterUsers(eligibleUsers.involved).length})
              </TabsTrigger>
              <TabsTrigger value="others" data-testid="review-stages-picker-tab-others">
                All active users ({filterUsers(eligibleUsers.others).length})
              </TabsTrigger>
            </TabsList>
            {['involved', 'others'].map((tab) => {
              const list = filterUsers(tab === 'involved' ? eligibleUsers.involved : eligibleUsers.others);
              return (
                <TabsContent key={tab} value={tab}>
                  <div className="max-h-[40vh] overflow-y-auto space-y-1 mt-1">
                    {list.length === 0 ? (
                      <p className="text-sm text-slate-500 italic p-3 text-center">No matching users.</p>
                    ) : list.map((u) => (
                      <button
                        type="button"
                        key={u.user_id}
                        onClick={() => addUser(u)}
                        className="w-full flex items-center justify-between p-2 rounded hover:bg-blue-50 text-left border border-transparent hover:border-blue-200 transition"
                        data-testid={`review-stage-user-pick-${u.user_id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{u.user_name}</div>
                          <div className="text-xs text-slate-500 truncate">{u.email}</div>
                        </div>
                        <Badge variant="outline" className="text-[10px] flex-shrink-0 ml-2">{u.user_role}</Badge>
                      </button>
                    ))}
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPickerOpen(false); setSearch(''); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ReviewStagesEditor;
